/**
 * @module builder
 */
import type { TieredPackageProductRule, TieredPackageTierId } from 'paintscout'
import { uuid } from '../../util'

/**
 * Sentinel `rateSection` for rules that target line items instead of a rate category.
 *
 * Line items carry no rate section, so they get their own entry in the rate-category dropdown and
 * their own rules. A rule scoped here applies to every line item on the estimate and to no area
 * substrate. Prefixed and bracketed so it can never collide with a real rate-section key (those are
 * uuids, or human-authored strings on old accounts).
 */
export const LINE_ITEMS_RATE_SECTION = '__lineItems__'

export function isLineItemsRateSection(rateSection?: string): boolean {
  return rateSection === LINE_ITEMS_RATE_SECTION
}

/**
 * Sentinel `rateSection` for a rule written against a product directly, with no category scope.
 *
 * The alternative to organising by rate category: pick any product from the library and map it 1:1,
 * and it swaps wherever it appears — every rate section and every line item. Both styles are stored
 * in the same `productRules` array and can coexist on one template, so switching the editor between
 * them never discards the other's rules.
 */
export const ALL_PRODUCTS_RATE_SECTION = '__allProducts__'

export function isAllProductsRateSection(rateSection?: string): boolean {
  return rateSection === ALL_PRODUCTS_RATE_SECTION
}

/**
 * Editing helpers for a package set's product rules.
 *
 * A rule is identified by the pair it is scoped to — its rate section and its base product — not by
 * its uuid, because that pair is what the Settings UI has in hand: it lists the products found on the
 * section's rates and writes a tier slot against one of them.
 */

export function getProductRule(args: {
  productRules?: TieredPackageProductRule[]
  rateSection: string
  baseProductKey: string
}): TieredPackageProductRule | undefined {
  const { productRules, rateSection, baseProductKey } = args

  return productRules?.find((rule) => rule?.rateSection === rateSection && rule?.baseProductKey === baseProductKey)
}

/** Every product key the rule set touches in one section, base products and tier slots alike. */
function getSectionProductKeys(args: {
  productRules?: TieredPackageProductRule[]
  rateSection: string
  exceptBaseProductKey?: string
}): string[] {
  const { productRules, rateSection, exceptBaseProductKey } = args

  return (productRules ?? [])
    .filter((rule) => rule?.rateSection === rateSection && rule?.baseProductKey !== exceptBaseProductKey)
    .reduce((acc: string[], rule) => {
      acc.push(rule.baseProductKey, ...Object.values(rule.products ?? {}).filter(Boolean))
      return acc
    }, [])
}

/**
 * Whether picking `productKey` here would make the mapping ambiguous.
 *
 * Moving between tiers is a lookup in both directions, so a product may appear at most once per rate
 * section across the whole rule set. If two rules in one section both put "Emerald" on Best, then
 * coming back from Best cannot tell which base product it came from.
 */
export function isProductRuleConflict(args: {
  productRules?: TieredPackageProductRule[]
  rateSection: string
  baseProductKey: string
  productKey: string
}): boolean {
  const { productRules, rateSection, baseProductKey, productKey } = args

  if (!productKey || productKey === baseProductKey) {
    return false
  }

  // Another rule's base product or slot in the same section.
  if (getSectionProductKeys({ productRules, rateSection, exceptBaseProductKey: baseProductKey }).includes(productKey)) {
    return true
  }

  // Or another tier of this same rule.
  const rule = getProductRule({ productRules, rateSection, baseProductKey })
  return Object.values(rule?.products ?? {}).includes(productKey)
}

/**
 * Set (or clear) one tier's product for one base product, returning the new rule list.
 *
 * Passing an empty `productKey` clears that tier. A rule with nothing left but its base product is
 * dropped, so clearing every slot leaves no dead rule behind.
 */
export function setProductRuleTier(args: {
  productRules?: TieredPackageProductRule[]
  rateSection: string
  baseProductKey: string
  tierId: TieredPackageTierId
  productKey?: string
}): TieredPackageProductRule[] {
  const { productRules, rateSection, baseProductKey, tierId, productKey } = args

  const rules = productRules ?? []
  const existing = getProductRule({ productRules: rules, rateSection, baseProductKey })

  const products = { ...(existing?.products ?? {}) }
  if (productKey && productKey !== baseProductKey) {
    products[tierId] = productKey
  } else {
    delete products[tierId]
  }

  const isEmpty = !Object.values(products).filter(Boolean).length

  if (!existing) {
    return isEmpty ? rules : [...rules, { id: uuid(), rateSection, baseProductKey, products }]
  }

  return rules.reduce((acc: TieredPackageProductRule[], rule) => {
    if (rule !== existing) {
      acc.push(rule)
    } else if (!isEmpty) {
      acc.push({ ...rule, products })
    }

    return acc
  }, [])
}
